<template>
  <!-- Wrapper div para evitar warning de Transition con v-dialog -->
  <div v-if="currentFeature">
    <component
      :is="featureComponent"
      :key="currentFeature.feature_key"
      :feature="currentFeature"
      @close="handleClose"
    />
  </div>
</template>

<script setup lang="ts">
  /**
   * @author Omar Sánchez Martínez
   * @since 2025-11-28
   * @description Renderiza dinámicamente el componente de feature según su tipo.
   * Se coloca en App.vue para mostrar features globales.
   */
  import { computed, defineAsyncComponent, type Component } from 'vue';
  import { useFeaturesStore } from '../stores';
  import { FeatureType } from '../types';

  const store = useFeaturesStore();

  // Obtiene el primer feature pendiente (ordenado por prioridad desde el API)
  const currentFeature = computed(() => store.features[0] ?? null);

  /**
   * Resuelve dinámicamente el componente según el tipo de feature
   */
  const featureComponent = computed<Component | null>(() => {
    if (!currentFeature.value) return null;

    const { type } = currentFeature.value;

    switch (type) {
      case FeatureType.MODAL:
        return defineAsyncComponent(() => import('./FeatureModal.vue'));
      case FeatureType.TOOLTIP:
        return defineAsyncComponent(() => import('./FeatureTooltip.vue'));
      case FeatureType.BANNER:
        return defineAsyncComponent(() => import('./FeatureBanner.vue'));
      case FeatureType.SPOTLIGHT:
        return defineAsyncComponent(() => import('./FeatureSpotlight.vue'));
      default:
        console.warn(`Feature type "${type}" not implemented yet`);
        return null;
    }
  });

  /**
   * Maneja el cierre del feature y lo marca como visto
   */
  const handleClose = async (): Promise<void> => {
    if (currentFeature.value) {
      await store.markAsViewed(currentFeature.value.feature_key);
    }
  };
</script>
